/**
 * Notice letters — 4 correspondence templates.
 *
 * These are letters, not agreements. They are formatted as correspondence — letterhead, date,
 * delivery line, recipient block, RE: line, numbered body, one signature block — and there is no
 * counter-signature, because nobody signs a notice back.
 *
 * The one thing that makes or breaks all four is the notice clause of the agreement the letter
 * concerns. Method, address, required copies and how the period is counted are all set by that
 * clause, and a letter that misses any of them can fail to do the job it was sent to do. Every
 * template therefore forces the founder to look it up: [DELIVERY METHOD], [NOTICE SECTION] and
 * [NOTICE ADDRESS] are blanks that cannot be filled from memory.
 *
 * Nothing in these letters states a legal conclusion, asserts a consequence the agreement does not
 * provide, or predicts an outcome. What the sender intends to do next is a blank the sender fills
 * in, and it should name only something the sender is actually prepared to do.
 *
 * Every body uses visible [BRACKET] blanks. An unfilled blank stays on the page on purpose.
 * The renderer prepends the standard disclaimer; nothing here competes with it.
 */

export const NOTICE_TEMPLATES = [
  {
    id: "notice-to-cure",
    docId: "notice-to-cure",
    title: "NOTICE TO CURE",
    version: "1.0",
    lastReviewed: "2026-08-25",

    founderGuide: {
      useThisWhen:
        "The other side has failed to do something the agreement requires, the agreement gives them a period to fix it before you can terminate or claim, and you intend to start that clock running.",
      whyItMatters:
        "Read the agreement's own notice clause before you send anything: sending by the wrong method, to the wrong address, without a required copy, or with the wrong cure period can waste the very right you are trying to exercise. This is the most procedurally fragile letter in the library. The failure looks like this — you email in March, terminate in May, and the distributor argues the cure period never started because notice had to go by courier to a different address with a copy to their counsel. The cure period, the delivery method, the address and what counts as a cure all come from the contract, not from this template. Once the other side has counsel involved, or once the money at stake is real, you should have counsel too: this is a document they will keep, quote back and hand to their lawyer.",
      negotiateCarefully: [
        {
          point: "Read the notice clause first, and follow it literally",
          detail:
            "Find the notice provision, write down the method, the address, any copy-to requirement and the deemed-receipt rule, and follow all four. If the clause permits several methods, use every one of them. This single step is worth more than everything else in the letter.",
        },
        {
          point: "Cite the right section and describe the right failure",
          detail:
            "Name the specific obligation and quote or paraphrase it accurately, then set out what happened with dates, quantities, lot codes, invoice numbers and amounts. A general complaint with no clause and no evidence gets answered on the vagueness rather than the substance.",
        },
        {
          point: "Say what a cure would actually be",
          detail:
            "Write down what would resolve it and how it will be measured — a payment received, a shipment delivered to specification, a service level held over a stated period. If you do not, a partial fix becomes an argument about whether the notice was satisfied.",
        },
        {
          point: "Count the cure period from deemed receipt, not from the day you sent it",
          detail:
            "Most clauses treat a notice as received on a stated day depending on the method used. Work the end date out from that rule, put the date in the letter, and diarise it. Acting a day early can undo everything that follows.",
        },
        {
          point: "Decide now what you do if it is not cured, and behave consistently",
          detail:
            "Name only a next step you are prepared to take, and decide before you send whether you keep shipping in the meantime. Placing orders through the cure period sends a very different message from the one in the letter.",
        },
      ],
      redFlags: [
        "You have not read the notice clause, or you cannot find it — stop and get help before sending.",
        "Termination is the realistic next step, which makes this the letter the whole exit depends on.",
        "The failure may not be curable at all, so a cure notice may be the wrong route to what you want.",
        "The other side already has lawyers involved on this issue.",
        "The amount at stake, or the value of the contract you would be ending, is material to the business.",
        "There is a quality or safety dimension, which runs on its own operational timetable regardless of any cure period.",
      ],
      timeToComplete: "45 minutes to draft · read the notice clause first",
    },

    placeholders: [
      { token: "[COMPANY NAME]", label: "Your legal entity name", brandKey: "legalEntityName", example: "Acme Beverage Co., LLC" },
      { token: "[COMPANY ADDRESS]", label: "Your address", brandKey: "principalAddress", example: "123 Main St, Austin, TX 78701" },
      { token: "[BRAND NAME]", label: "Your brand name", brandKey: "tradeName", example: "Acme" },
      { token: "[NOTICE DATE]", label: "Date of this letter", example: "March 4, 2026" },
      { token: "[DELIVERY METHOD]", label: "Delivery method the agreement requires — look it up", example: "overnight courier with signature, and email to the address in the notice clause" },
      { token: "[NOTICE SECTION]", label: "Section number of the notice clause", example: "14.2" },
      { token: "[NOTICE ADDRESS]", label: "Notice address from the agreement — not the sales contact", example: "Attn: Legal Department, 500 Industrial Way, Columbus, OH 43215" },
      { token: "[RECIPIENT NAME]", label: "Person the notice clause names", example: "Priya Raman" },
      { token: "[RECIPIENT TITLE]", label: "Their title", example: "General Counsel" },
      { token: "[COUNTERPARTY NAME]", label: "Other party's legal name", example: "Midwest Distributing, Inc." },
      { token: "[COPY TO RECIPIENT]", label: "Any copy the notice clause requires", example: "Ramirez & Hall LLP, 200 Congress Ave, Austin, TX 78701 — required by Section 14.2" },
      { token: "[AGREEMENT NAME]", label: "Name of the agreement", example: "Distribution Agreement" },
      { token: "[AGREEMENT DATE]", label: "Date of the agreement", example: "January 15, 2025" },
      { token: "[AGREEMENT SUBJECT]", label: "What the agreement covers", example: "you distribute our products in the territory described in Exhibit A" },
      { token: "[BREACHED SECTION]", label: "Section not complied with", example: "5.3" },
      { token: "[OBLIGATION DESCRIPTION]", label: "What that section requires", example: "payment of each invoice within the stated terms of the invoice date" },
      { token: "[FAILURE DESCRIPTION]", label: "What happened, factually", example: "Eleven invoices remain unpaid beyond their due dates, and no payment has been received since the date below" },
      { token: "[FAILURE DATES]", label: "The dates involved", example: "invoices dated November 3, 2025 through February 6, 2026; last payment received December 12, 2025" },
      { token: "[AMOUNT AT ISSUE]", label: "Amount or quantity involved", example: "$84,320.00 across eleven invoices" },
      { token: "[CURE REQUIRED]", label: "What would count as a cure, stated concretely", example: "payment in full of the eleven invoices listed in the enclosed schedule, in cleared funds" },
      { token: "[CURE EVIDENCE]", label: "How the cure must be evidenced", example: "cleared funds in our account and a remittance advice identifying each invoice paid" },
      { token: "[CURE SECTION]", label: "Section providing the cure period", example: "12.1" },
      { token: "[CURE PERIOD]", label: "Cure period the agreement gives", example: "30 days" },
      { token: "[CURE END DATE]", label: "Date the cure period ends, counted from deemed receipt", example: "April 6, 2026" },
      { token: "[REMEDY SECTION]", label: "Section setting out what is available to you if it is not cured", example: "12.2" },
      { token: "[INTENDED NEXT STEP]", label: "What you intend to do, stated neutrally", example: "exercise our right to terminate the Agreement and to stop further shipments" },
      { token: "[INTERIM POSITION]", label: "Whether you keep performing during the cure period", example: "we will continue to fulfil accepted orders on prepayment terms only, and will place no new orders into production" },
      { token: "[RESPONSE CONTACT NAME]", label: "Who should be contacted about this letter", example: "Sam Ortiz, Chief Operating Officer" },
      { token: "[RESPONSE CONTACT DETAILS]", label: "Their phone and email", example: "512-555-0142 / sam@example.com" },
      { token: "[SIGNATORY NAME]", label: "Who signs the letter", example: "Sam Ortiz" },
      { token: "[SIGNATORY TITLE]", label: "Their title", example: "Chief Operating Officer" },
      { token: "[ENCLOSURES]", label: "What is enclosed", example: "schedule of unpaid invoices; copies of invoices 1104 through 1211; account statement" },
    ],

    body: `[COMPANY NAME]
[COMPANY ADDRESS]


[NOTICE DATE]


DELIVERED BY: [DELIVERY METHOD]
Sent in the manner required by Section [NOTICE SECTION] of the Agreement identified below.


[RECIPIENT NAME]
[RECIPIENT TITLE]
[COUNTERPARTY NAME]
[NOTICE ADDRESS]

COPY TO: [COPY TO RECIPIENT]


RE:  NOTICE TO CURE — [AGREEMENT NAME] dated [AGREEMENT DATE]


Dear [RECIPIENT NAME]:

This letter is formal notice under Section [BREACHED SECTION] and Section [CURE SECTION] of the
Agreement described below. Please read it in full and treat it as a notice given under the
Agreement rather than as ordinary correspondence.

1. THE AGREEMENT
[COMPANY NAME] and [COUNTERPARTY NAME] are parties to the [AGREEMENT NAME] dated
[AGREEMENT DATE] (the "Agreement"), under which [AGREEMENT SUBJECT] in connection with our
[BRAND NAME] products.

2. THE OBLIGATION
Section [BREACHED SECTION] of the Agreement requires [OBLIGATION DESCRIPTION].

3. WHAT HAS HAPPENED
[FAILURE DESCRIPTION].

Dates: [FAILURE DATES].

Amount or quantity involved: [AMOUNT AT ISSUE].

The documents enclosed with this letter support the account above. If any part of it is factually
wrong, please tell us in writing, with the documents you rely on, as soon as possible.

4. WHAT WE REQUIRE
To cure the failure described in Section 3, [COUNTERPARTY NAME] must do the following:
[CURE REQUIRED].

We will treat the failure as cured when we have [CURE EVIDENCE]. A partial or conditional response
will not, on its own, be treated as a cure.

5. THE CURE PERIOD
Section [CURE SECTION] of the Agreement provides a cure period of [CURE PERIOD] following notice.
Calculated from the date this notice is treated as received under Section [NOTICE SECTION] of the
Agreement, that period ends on [CURE END DATE].

If the calculation of that date differs under the Agreement, the period is the one the Agreement
provides, and nothing in this letter shortens or extends it.

6. IF THE FAILURE IS NOT CURED
If the failure is not cured by the end of the cure period, we intend to exercise the rights
available to us under Section [REMEDY SECTION] of the Agreement, which for our part means we
intend to [INTENDED NEXT STEP]. We would rather resolve this within the cure period, and we are
available to discuss it at any point before it ends.

7. PERFORMANCE IN THE MEANTIME
During the cure period: [INTERIM POSITION]. Anything we continue to do during this period is done
without prejudice to this notice and does not indicate that the failure has been resolved.

8. RESERVATION OF RIGHTS
Nothing in this letter, and nothing we do or decline to do in connection with it, waives, limits,
modifies or elects any right or remedy available to us under the Agreement or otherwise, all of
which are expressly reserved. No delay in acting, no acceptance of partial performance and no
continued performance by us operates as a waiver of any right, including any right arising from
this failure or from any other failure, whether or not described here. This letter is not a
complete statement of every matter between the parties.

9. HOW TO RESPOND
Please direct any response to [RESPONSE CONTACT NAME], [RESPONSE CONTACT DETAILS], and copy any
address the Agreement requires for notices to us. A response by email is welcome, but it does not
replace any formal notice the Agreement requires you to give.


Sincerely,



_______________________________________
[SIGNATORY NAME]
[SIGNATORY TITLE]
[COMPANY NAME]


Enclosures: [ENCLOSURES]
`,
  },

  {
    id: "breach-notice",
    docId: "breach-notice",
    title: "NOTICE OF BREACH",
    version: "1.0",
    lastReviewed: "2026-08-25",

    founderGuide: {
      useThisWhen:
        "Something the agreement requires has not been done, and you want it formally on the record — either because the failure is not one that can be undone, or because you are documenting it while you work out what to do next and do not want to be treated as having let it go.",
      whyItMatters:
        "Read the agreement's own notice clause before you send anything: sending by the wrong method, to the wrong address, without a required copy, or with the wrong cure period can waste the very right you are trying to exercise. The value of this letter is almost entirely procedural. Most agreements condition the remedies you actually care about — termination, damages, stopping performance — on having given notice the way the contract describes, and some of them impose a deadline for giving it. Founders skip this step because the relationship still feels workable, then find months later that the right they were counting on was never preserved. Once the other side has counsel involved, or once the money at stake is real, you should have counsel too: this is a document they will keep, quote back and hand to their lawyer.",
      negotiateCarefully: [
        {
          point: "Read the notice clause first, and follow it literally",
          detail:
            "Method, address, copy-to and deemed receipt are all set by the contract. Write them down before you draft a word, use every method the clause permits, and send any required copy on the same day.",
        },
        {
          point: "Be precise about what you are and are not doing",
          detail:
            "Say plainly whether you are starting a cure period, reserving your position while you assess, or treating the failure as one that cannot be cured. Ambiguity here is what turns a clean record into an argument about what the letter meant.",
        },
        {
          point: "Evidence beats adjectives",
          detail:
            "Name the section, set out dates, quantities, lot codes, invoice numbers and amounts, and attach the documents. For a specification failure, reference the signed specification and the lot rather than describing the product as wrong.",
        },
        {
          point: "Ask them to preserve records now",
          detail:
            "Batch records, temperature logs, retained samples, delivery records and correspondence go missing on ordinary retention schedules. Asking for them to be preserved costs nothing and is much harder to arrange later.",
        },
        {
          point: "Make your conduct match the letter",
          detail:
            "Decide before you send whether you keep shipping, keep ordering and keep extending credit, and then be consistent. Continuing exactly as before is the fastest way to undermine the notice you just gave.",
        },
      ],
      redFlags: [
        "You have not read the notice clause, or you are unsure whether a deadline for giving notice has already run.",
        "You are unsure whether the failure is capable of cure, which changes what this letter should say.",
        "The other side already has lawyers involved on this issue.",
        "The failure has a quality or safety dimension, which needs an operational response on its own timetable.",
        "You are considering terminating, which makes what this letter says the foundation of the exit.",
        "The amount at stake is material to the business.",
      ],
      timeToComplete: "45 minutes to draft · read the notice clause first",
    },

    placeholders: [
      { token: "[COMPANY NAME]", label: "Your legal entity name", brandKey: "legalEntityName", example: "Acme Beverage Co., LLC" },
      { token: "[COMPANY ADDRESS]", label: "Your address", brandKey: "principalAddress", example: "123 Main St, Austin, TX 78701" },
      { token: "[BRAND NAME]", label: "Your brand name", brandKey: "tradeName", example: "Acme" },
      { token: "[NOTICE DATE]", label: "Date of this letter", example: "March 4, 2026" },
      { token: "[DELIVERY METHOD]", label: "Delivery method the agreement requires — look it up", example: "certified mail, return receipt requested, and email to the address in the notice clause" },
      { token: "[NOTICE SECTION]", label: "Section number of the notice clause", example: "16.4" },
      { token: "[NOTICE ADDRESS]", label: "Notice address from the agreement — not the day-to-day contact", example: "Attn: President, 88 Plant Rd, Fresno, CA 93725" },
      { token: "[RECIPIENT NAME]", label: "Person the notice clause names", example: "Alan Whitfield" },
      { token: "[RECIPIENT TITLE]", label: "Their title", example: "President" },
      { token: "[COUNTERPARTY NAME]", label: "Other party's legal name", example: "Valley Co-Packing, Inc." },
      { token: "[COPY TO RECIPIENT]", label: "Any copy the notice clause requires", example: "no copy required by the Agreement" },
      { token: "[AGREEMENT NAME]", label: "Name of the agreement", example: "Co-Packing Agreement" },
      { token: "[AGREEMENT DATE]", label: "Date of the agreement", example: "August 2, 2024" },
      { token: "[AGREEMENT SUBJECT]", label: "What the agreement covers", example: "you manufacture and pack our products to the specification attached to it" },
      { token: "[BREACHED SECTION]", label: "Section not complied with", example: "4.1 and Exhibit B" },
      { token: "[OBLIGATION DESCRIPTION]", label: "What that section requires", example: "that each batch conform to the signed specification and that a certificate of analysis accompany every release" },
      { token: "[FAILURE DESCRIPTION]", label: "What happened, factually", example: "Three production lots were released without a certificate of analysis, and two of those lots were outside the specification range on fill volume" },
      { token: "[FAILURE DATES]", label: "The dates involved", example: "lots produced January 9, January 23 and February 11, 2026; discovered on February 18, 2026" },
      { token: "[AMOUNT AT ISSUE]", label: "Amount, quantity or lots involved", example: "lots 26009, 26023 and 26042, totalling 4,180 cases" },
      { token: "[NOTICE PURPOSE]", label: "What this letter is for", example: "to place you on notice of the failure and to preserve our position while we complete our assessment" },
      { token: "[CURABILITY POSITION]", label: "Your position on whether the failure can be cured", example: "Product already shipped to retail cannot be unshipped, so we do not treat this failure as one capable of cure, and this letter does not start a cure period" },
      { token: "[WHAT WE REQUIRE NOW]", label: "What you want from them at this stage", example: "a written account of what happened on each lot, the batch and line records for those lots, and confirmation of what has been shipped and to whom" },
      { token: "[RESPONSE DEADLINE]", label: "When you want that by", example: "March 18, 2026" },
      { token: "[RECORDS TO PRESERVE]", label: "Records they should preserve", example: "batch and line records, fill checks, sanitation and changeover logs, retained samples, certificates of analysis, shipping records and all related correspondence" },
      { token: "[INTERIM POSITION]", label: "What you are doing in the meantime", example: "We have placed the affected lots on hold at our warehouse and have suspended the next scheduled run pending your response" },
      { token: "[RESPONSE CONTACT NAME]", label: "Who should be contacted about this letter", example: "Dana Weller, Head of Operations" },
      { token: "[RESPONSE CONTACT DETAILS]", label: "Their phone and email", example: "512-555-0188 / dana@example.com" },
      { token: "[SIGNATORY NAME]", label: "Who signs the letter", example: "Dana Weller" },
      { token: "[SIGNATORY TITLE]", label: "Their title", example: "Head of Operations" },
      { token: "[ENCLOSURES]", label: "What is enclosed", example: "signed specification; fill volume test results dated February 18, 2026; release records for the three lots" },
    ],

    body: `[COMPANY NAME]
[COMPANY ADDRESS]


[NOTICE DATE]


DELIVERED BY: [DELIVERY METHOD]
Sent in the manner required by Section [NOTICE SECTION] of the Agreement identified below.


[RECIPIENT NAME]
[RECIPIENT TITLE]
[COUNTERPARTY NAME]
[NOTICE ADDRESS]

COPY TO: [COPY TO RECIPIENT]


RE:  NOTICE OF BREACH — [AGREEMENT NAME] dated [AGREEMENT DATE]


Dear [RECIPIENT NAME]:

This letter is formal notice under Section [BREACHED SECTION] of the Agreement described below.
Please read it in full and treat it as a notice given under the Agreement rather than as ordinary
correspondence.

1. THE AGREEMENT
[COMPANY NAME] and [COUNTERPARTY NAME] are parties to the [AGREEMENT NAME] dated
[AGREEMENT DATE] (the "Agreement"), under which [AGREEMENT SUBJECT] in connection with our
[BRAND NAME] products.

2. THE OBLIGATION
Section [BREACHED SECTION] of the Agreement requires [OBLIGATION DESCRIPTION].

3. WHAT HAS HAPPENED
[FAILURE DESCRIPTION].

Dates: [FAILURE DATES].

Amount, quantity or lots involved: [AMOUNT AT ISSUE].

The documents enclosed support the account above. If any part of it is factually wrong, please
tell us in writing, with the documents you rely on, as soon as possible.

4. WHY WE ARE WRITING
The purpose of this notice is [NOTICE PURPOSE].

5. OUR POSITION ON CURE
[CURABILITY POSITION].

Nothing in this Section is a statement about how the Agreement or any right under it would be
interpreted or applied. It states our present position so there is no doubt about what this letter
is and is not doing.

6. WHAT WE REQUIRE NOW
We ask that [COUNTERPARTY NAME] provide the following by [RESPONSE DEADLINE]:
[WHAT WE REQUIRE NOW].

7. PRESERVATION OF RECORDS
Please preserve, and instruct anyone holding them on your behalf to preserve, the following until
this matter is resolved: [RECORDS TO PRESERVE]. Please suspend any routine deletion or destruction
schedule that would affect them.

8. WHAT WE ARE DOING IN THE MEANTIME
[INTERIM POSITION]. Anything we continue to do is done without prejudice to this notice.

9. NO WAIVER; RESERVATION OF RIGHTS
Nothing in this letter waives, limits, modifies, releases or elects any right, remedy, claim or
defence available to [COMPANY NAME] under the Agreement or otherwise, all of which are expressly
reserved. In particular: this letter is not an election of any remedy; it is not a consent to or
approval of anything described in it; no delay in giving this notice or in acting on it is a
waiver; no acceptance of partial performance, continued performance, continued ordering or
continued payment by either party is a waiver; and the description of one failure is not a waiver
of any other failure, whether or not we know of it today. Any waiver by us must be in writing and
signed by an authorised officer of [COMPANY NAME].

10. HOW TO RESPOND
Please direct any response to [RESPONSE CONTACT NAME], [RESPONSE CONTACT DETAILS], and copy any
address the Agreement requires for notices to us. A response by email is welcome, but it does not
replace any formal notice the Agreement requires you to give.


Sincerely,



_______________________________________
[SIGNATORY NAME]
[SIGNATORY TITLE]
[COMPANY NAME]


Enclosures: [ENCLOSURES]
`,
  },

  {
    id: "notice-of-termination",
    docId: "notice-of-termination",
    title: "NOTICE OF TERMINATION",
    version: "1.0",
    lastReviewed: "2026-08-25",

    founderGuide: {
      useThisWhen:
        "You are ending an agreement by exercising a right inside it — for cause after an uncured failure, for convenience on notice, or by declining to renew before an automatic renewal date.",
      whyItMatters:
        "Read the agreement's own notice clause before you send anything: sending by the wrong method, to the wrong address, without a required copy, or with the wrong notice period can waste the very right you are trying to exercise. Termination is where a commercial disagreement most often turns into something larger. The right you exercise, the period you give, the reasons you state and the method you use all come from the contract, and where the counterparty sits abroad there may be protections the contract never mentions. The operational half matters just as much: a terminated distributor holds your inventory, your accounts and sometimes the retailer relationship, and an out-of-stock during a badly planned transition can cost the listing itself. Once the other side has counsel involved, or once the money at stake is real, you should have counsel too: this is a document they will keep, quote back and hand to their lawyer.",
      negotiateCarefully: [
        {
          point: "Read the notice clause first, and follow it literally",
          detail:
            "Method, address, copy-to and deemed receipt decide whether the termination lands. Use every method the clause permits, send any required copy the same day, and keep proof of delivery.",
        },
        {
          point: "Name the right you are exercising, and count the date from it",
          detail:
            "Cause, convenience and non-renewal carry different periods and different consequences. Cite the section, work the effective date out from deemed receipt, and put that date in the letter. A period even slightly short invites a challenge to the whole notice.",
        },
        {
          point: "Decide whether to state reasons at all",
          detail:
            "Reasons stated in a termination notice become the case you then have to stand behind. Where the agreement lets you terminate for convenience, saying more than the clause requires can create an argument you did not need to have — worth a conversation with counsel before it goes out.",
        },
        {
          point: "Put the wind-down in the letter, not in a later negotiation",
          detail:
            "Final orders, outstanding invoices, inventory, tooling, artwork, data, records and confidential information all need a stated deadline. Asking for the tooling back after termination is a much weaker position than asking for it in the notice.",
        },
        {
          point: "Plan the transition before the notice, not after",
          detail:
            "Who services the accounts, who ships the next order, who tells the retailer, and what happens to product already in the market. Set out the cooperation you expect and the period it runs for, and list the sections that survive so nobody argues about it later.",
        },
      ],
      redFlags: [
        "You have not read the termination, notice and renewal clauses in full.",
        "You are terminating for cause, which means the stated grounds have to hold up.",
        "An automatic renewal deadline is close, or you are not certain it has not already passed.",
        "The counterparty is in another country, where ending a distributor, agent or licensee relationship can carry consequences the contract does not describe.",
        "There is inventory, tooling or a retailer relationship sitting with the party you are terminating.",
        "The other side already has lawyers involved.",
      ],
      timeToComplete: "1 hour to draft · take advice before sending, not after",
    },

    placeholders: [
      { token: "[COMPANY NAME]", label: "Your legal entity name", brandKey: "legalEntityName", example: "Acme Beverage Co., LLC" },
      { token: "[COMPANY ADDRESS]", label: "Your address", brandKey: "principalAddress", example: "123 Main St, Austin, TX 78701" },
      { token: "[BRAND NAME]", label: "Your brand name", brandKey: "tradeName", example: "Acme" },
      { token: "[NOTICE DATE]", label: "Date of this letter", example: "April 8, 2026" },
      { token: "[DELIVERY METHOD]", label: "Delivery method the agreement requires — look it up", example: "overnight courier with signature, plus certified mail and email" },
      { token: "[NOTICE SECTION]", label: "Section number of the notice clause", example: "14.2" },
      { token: "[NOTICE ADDRESS]", label: "Notice address from the agreement — not the sales contact", example: "Attn: Legal Department, 500 Industrial Way, Columbus, OH 43215" },
      { token: "[RECIPIENT NAME]", label: "Person the notice clause names", example: "Priya Raman" },
      { token: "[RECIPIENT TITLE]", label: "Their title", example: "General Counsel" },
      { token: "[COUNTERPARTY NAME]", label: "Other party's legal name", example: "Midwest Distributing, Inc." },
      { token: "[COPY TO RECIPIENT]", label: "Any copy the notice clause requires", example: "Ramirez & Hall LLP, 200 Congress Ave, Austin, TX 78701 — required by Section 14.2" },
      { token: "[AGREEMENT NAME]", label: "Name of the agreement", example: "Distribution Agreement" },
      { token: "[AGREEMENT DATE]", label: "Date of the agreement", example: "January 15, 2025" },
      { token: "[AGREEMENT SUBJECT]", label: "What the agreement covers", example: "you distribute our products in the territory described in Exhibit A" },
      { token: "[TERMINATION SECTION]", label: "Termination provision relied on", example: "12.2" },
      { token: "[TERMINATION BASIS]", label: "Which right you are exercising", example: "termination for cause, following our Notice to Cure dated March 4, 2026, which was not cured by the end of the cure period" },
      { token: "[TERMINATION GROUNDS]", label: "The grounds, where cause is relied on", example: "eleven invoices totalling $84,320.00 remained unpaid at the end of the cure period on April 6, 2026" },
      { token: "[NOTICE PERIOD]", label: "Notice period the provision requires", example: "60 days" },
      { token: "[TERMINATION EFFECTIVE DATE]", label: "Effective date, counted from deemed receipt", example: "June 12, 2026" },
      { token: "[INTERIM POSITION]", label: "Orders, shipments and credit between now and the effective date", example: "no new orders will be accepted; accepted orders already in production will ship on prepayment terms only" },
      { token: "[FINAL ORDER DEADLINE]", label: "Last date for final orders, if any", example: "April 30, 2026, for delivery before the effective date" },
      { token: "[OUTSTANDING BALANCE]", label: "Amount outstanding, if any", example: "$84,320.00 as at the date of this letter, per the enclosed statement" },
      { token: "[PAYMENT DEADLINE]", label: "When outstanding amounts must be paid", example: "the effective date" },
      { token: "[INVENTORY TREATMENT]", label: "What happens to inventory the counterparty holds", example: "provide a dated inventory report within 10 days; saleable inventory in date code may be sold through until the effective date; we will repurchase the balance at the price you paid, less freight" },
      { token: "[MATERIALS TO RETURN]", label: "Materials, tooling and data to be returned", example: "point-of-sale materials, artwork files, tooling and plates, price lists, the customer and account list, and all confidential information" },
      { token: "[RETURN DEADLINE]", label: "When those must be returned", example: "within 15 days after the effective date" },
      { token: "[TRANSITION SUPPORT]", label: "Transition cooperation expected", example: "introductions to each retail and account contact, transfer of open orders and promotional commitments, and access to sales and depletion data for the last 24 months" },
      { token: "[SURVIVING SECTIONS]", label: "Sections that survive termination", example: "Sections 9, 10, 13 and 15" },
      { token: "[RESPONSE CONTACT NAME]", label: "Who should be contacted about this letter", example: "Sam Ortiz, Chief Operating Officer" },
      { token: "[RESPONSE CONTACT DETAILS]", label: "Their phone and email", example: "512-555-0142 / sam@example.com" },
      { token: "[SIGNATORY NAME]", label: "Who signs the letter", example: "Sam Ortiz" },
      { token: "[SIGNATORY TITLE]", label: "Their title", example: "Chief Operating Officer" },
      { token: "[ENCLOSURES]", label: "What is enclosed", example: "Notice to Cure dated March 4, 2026; account statement; inventory report template" },
    ],

    body: `[COMPANY NAME]
[COMPANY ADDRESS]


[NOTICE DATE]


DELIVERED BY: [DELIVERY METHOD]
Sent in the manner required by Section [NOTICE SECTION] of the Agreement identified below.


[RECIPIENT NAME]
[RECIPIENT TITLE]
[COUNTERPARTY NAME]
[NOTICE ADDRESS]

COPY TO: [COPY TO RECIPIENT]


RE:  NOTICE OF TERMINATION — [AGREEMENT NAME] dated [AGREEMENT DATE]


Dear [RECIPIENT NAME]:

This letter is formal notice of termination given under Section [TERMINATION SECTION] of the
Agreement described below. Please read it in full and treat it as a notice given under the
Agreement rather than as ordinary correspondence.

1. THE AGREEMENT
[COMPANY NAME] and [COUNTERPARTY NAME] are parties to the [AGREEMENT NAME] dated
[AGREEMENT DATE] (the "Agreement"), under which [AGREEMENT SUBJECT] in connection with our
[BRAND NAME] products.

2. THE PROVISION RELIED ON
[COMPANY NAME] terminates the Agreement under Section [TERMINATION SECTION]. The basis is
[TERMINATION BASIS].

Grounds, where relied on: [TERMINATION GROUNDS].

3. EFFECTIVE DATE
Section [TERMINATION SECTION] requires [NOTICE PERIOD] notice. Calculated from the date this
notice is treated as received under Section [NOTICE SECTION] of the Agreement, termination takes
effect on [TERMINATION EFFECTIVE DATE] (the "Effective Date").

If the Agreement produces a different date, the date the Agreement produces applies, and nothing
in this letter shortens the period the Agreement requires.

4. BETWEEN NOW AND THE EFFECTIVE DATE
Orders, shipments and credit will be handled as follows: [INTERIM POSITION].

Final orders, if any, must be placed by [FINAL ORDER DEADLINE]. Both parties remain bound by the
Agreement until the Effective Date, and performance during this period is without prejudice to
this notice.

5. AMOUNTS OUTSTANDING
Amounts outstanding as at the date of this letter: [OUTSTANDING BALANCE]. Please pay all amounts
due, including any that fall due before the Effective Date, by [PAYMENT DEADLINE]. Please also
send a final statement of any amount you consider due to you, with supporting documentation, by
the same date, so that the account can be reconciled rather than argued about afterwards.

6. INVENTORY
Inventory of our products held by [COUNTERPARTY NAME] will be dealt with as follows:
[INVENTORY TREATMENT].

Until it is dealt with, please continue to store the product under the conditions the Agreement
requires, keep it within its date codes, and keep lot-level records so that any withdrawal can
reach it.

7. MATERIALS, DATA AND CONFIDENTIAL INFORMATION
Please return or, where we direct, securely destroy the following: [MATERIALS TO RETURN]. This
must be completed by [RETURN DEADLINE], and confirmed to us in writing. Confidentiality
obligations continue according to their terms regardless of termination.

8. TRANSITION
We ask for the following cooperation through the Effective Date and for a reasonable period after
it: [TRANSITION SUPPORT]. Our aim is a transition that does not create out-of-stocks at retail,
and we will work with you to achieve that.

9. SURVIVAL
The following provisions of the Agreement survive termination according to their terms, together
with any other provision that by its nature is intended to survive: [SURVIVING SECTIONS].

10. RESERVATION OF RIGHTS
Nothing in this letter waives, limits, modifies, releases or elects any right, remedy, claim or
defence available to [COMPANY NAME] under the Agreement or otherwise, all of which are expressly
reserved, including any claim arising from events before the Effective Date. Termination of the
Agreement is without prejudice to any accrued right or obligation. Anything either party does
during the notice period is without prejudice to this notice, and no continued performance,
acceptance of payment or negotiation is a waiver. Any waiver by us must be in writing and signed
by an authorised officer of [COMPANY NAME].

11. HOW TO RESPOND
Please direct any response to [RESPONSE CONTACT NAME], [RESPONSE CONTACT DETAILS], and copy any
address the Agreement requires for notices to us. A response by email is welcome, but it does not
replace any formal notice the Agreement requires you to give.


Sincerely,



_______________________________________
[SIGNATORY NAME]
[SIGNATORY TITLE]
[COMPANY NAME]


Enclosures: [ENCLOSURES]
`,
  },

  {
    id: "demand-letter",
    docId: "demand-letter",
    title: "DEMAND FOR PAYMENT",
    version: "1.0",
    lastReviewed: "2026-08-25",

    founderGuide: {
      useThisWhen:
        "Money is owed, informal chasing has stopped working, and you want a dated, specific record that a formal demand was made and what it asked for.",
      whyItMatters:
        "Read the agreement's own notice clause before you send anything: sending by the wrong method, to the wrong address, without a required copy, or with the wrong deadline can waste the very right you are trying to exercise. A demand letter is often the fastest way to get paid, because it moves the problem from somebody's inbox to somebody's desk. It is also a document the other side keeps, quotes back and shows to their lawyer, so an overstated number or a wrong invoice date lets them spend a month arguing about your arithmetic instead of their debt. Check whether the agreement requires a formal notice or a cure period first — a demand letter is not a substitute for one. Once the other side has counsel involved, or once the money at stake is real, you should have counsel too.",
      negotiateCarefully: [
        {
          point: "Read the notice clause first, and follow it literally",
          detail:
            "Even a payment demand can be the notice a later remedy depends on. Use the method and address the agreement requires, send any required copy, and keep proof of delivery.",
        },
        {
          point: "Show the arithmetic",
          detail:
            "State the number, then show how it is built: invoice by invoice, deduction by deduction, with dates and any credits you accept. Verify every figure against your records before it goes out, because one wrong number becomes the whole conversation.",
        },
        {
          point: "Attach the backup",
          detail:
            "Invoices, purchase orders, proofs of delivery, signed specifications, lot records and deduction detail. In CPG most of these disputes are about money that was taken rather than never paid, so the deduction backup is usually the document that decides it.",
        },
        {
          point: "Set a deadline you will actually act on",
          detail:
            "A deadline you let slide teaches them that your deadlines are decorative, which makes the next one worthless. Pick a date you can live with, diarise it, and name only a next step you are genuinely prepared to take.",
        },
        {
          point: "Decide about shipping before you send",
          detail:
            "Demanding payment while continuing to ship on open credit removes any urgency for the other side. Decide whether shipments continue, stop, or continue only on changed terms, say so in the letter, and then do that.",
        },
      ],
      redFlags: [
        "You have not checked whether the agreement requires a formal notice or a cure period before a demand.",
        "You have not reconciled the figures against your own records and the backup documents.",
        "You are tempted to name a consequence you are not prepared to follow through on.",
        "The other side already has lawyers involved, or has disputed the amount in writing.",
        "The amount is material to the business, or recovering it will decide whether you can pay your own suppliers.",
        "A documentation or claim window with a retailer or distributor may be about to close.",
      ],
      timeToComplete: "45 minutes to draft · reconcile the numbers first",
    },

    placeholders: [
      { token: "[COMPANY NAME]", label: "Your legal entity name", brandKey: "legalEntityName", example: "Acme Beverage Co., LLC" },
      { token: "[COMPANY ADDRESS]", label: "Your address", brandKey: "principalAddress", example: "123 Main St, Austin, TX 78701" },
      { token: "[BRAND NAME]", label: "Your brand name", brandKey: "tradeName", example: "Acme" },
      { token: "[NOTICE DATE]", label: "Date of this letter", example: "March 4, 2026" },
      { token: "[DELIVERY METHOD]", label: "Delivery method the agreement requires — look it up", example: "certified mail, return receipt requested, and email" },
      { token: "[NOTICE SECTION]", label: "Section number of the notice clause", example: "14.2" },
      { token: "[NOTICE ADDRESS]", label: "Notice address from the agreement — not the buyer's inbox", example: "Attn: Accounts Payable and Legal Department, 500 Industrial Way, Columbus, OH 43215" },
      { token: "[RECIPIENT NAME]", label: "Person the notice clause names", example: "Priya Raman" },
      { token: "[RECIPIENT TITLE]", label: "Their title", example: "Chief Financial Officer" },
      { token: "[COUNTERPARTY NAME]", label: "Other party's legal name", example: "Midwest Distributing, Inc." },
      { token: "[COPY TO RECIPIENT]", label: "Any copy the notice clause requires", example: "no copy required by the Agreement" },
      { token: "[AGREEMENT NAME]", label: "Agreement or arrangement the debt arises under", example: "Distribution Agreement" },
      { token: "[AGREEMENT DATE]", label: "Date of the agreement", example: "January 15, 2025" },
      { token: "[AGREEMENT SUBJECT]", label: "What the agreement covers", example: "you purchase and distribute our products in the territory described in Exhibit A" },
      { token: "[BASIS SECTION]", label: "Section requiring payment", example: "5.3" },
      { token: "[AMOUNT DEMANDED]", label: "Total amount demanded", example: "$84,320.00" },
      { token: "[AMOUNT CALCULATION]", label: "How the amount is built up", example: "$91,120.00 invoiced across eleven invoices, less $6,800.00 in credits we accept for two damaged pallets" },
      { token: "[INVOICE REFERENCES]", label: "Invoices or transactions concerned", example: "invoices 1104, 1119, 1127, 1138, 1146, 1155, 1163, 1178, 1190, 1204 and 1211" },
      { token: "[DISPUTED ITEMS]", label: "Deductions or chargebacks you do not accept, and why", example: "$12,400.00 deducted as unsaleables with no backup provided despite three requests" },
      { token: "[PAYMENT DEADLINE]", label: "Date payment must be received", example: "March 25, 2026" },
      { token: "[PAYMENT METHOD]", label: "How to pay", example: "ACH or wire transfer in cleared funds" },
      { token: "[REMITTANCE DETAILS]", label: "Where to send it and what to reference", example: "the account on each invoice, referencing each invoice number, with remittance advice to ar@example.com" },
      { token: "[DISPUTE RESPONSE DEADLINE]", label: "Date by which any dispute must be documented", example: "March 18, 2026" },
      { token: "[INTENDED NEXT STEP]", label: "What you intend to do if unpaid, stated neutrally", example: "place the account with a collection agent and take advice on the further options available to us under the Agreement" },
      { token: "[SHIPMENT POSITION]", label: "Whether shipments continue", example: "Shipments are suspended with immediate effect and will resume only on prepayment terms" },
      { token: "[RESPONSE CONTACT NAME]", label: "Who should be contacted about this letter", example: "Sam Ortiz, Chief Operating Officer" },
      { token: "[RESPONSE CONTACT DETAILS]", label: "Their phone and email", example: "512-555-0142 / sam@example.com" },
      { token: "[SIGNATORY NAME]", label: "Who signs the letter", example: "Sam Ortiz" },
      { token: "[SIGNATORY TITLE]", label: "Their title", example: "Chief Operating Officer" },
      { token: "[ENCLOSURES]", label: "What is enclosed", example: "account statement; copies of the eleven invoices; proofs of delivery; deduction detail requests dated January 8, January 26 and February 17, 2026" },
    ],

    body: `[COMPANY NAME]
[COMPANY ADDRESS]


[NOTICE DATE]


DELIVERED BY: [DELIVERY METHOD]
Sent in the manner required by Section [NOTICE SECTION] of the Agreement identified below.


[RECIPIENT NAME]
[RECIPIENT TITLE]
[COUNTERPARTY NAME]
[NOTICE ADDRESS]

COPY TO: [COPY TO RECIPIENT]


RE:  DEMAND FOR PAYMENT — [AMOUNT DEMANDED] outstanding under the [AGREEMENT NAME]


Dear [RECIPIENT NAME]:

This letter is a formal demand for payment. Please read it in full and treat it as a notice given
under the Agreement described below rather than as ordinary correspondence.

1. THE AGREEMENT
[COMPANY NAME] and [COUNTERPARTY NAME] are parties to the [AGREEMENT NAME] dated
[AGREEMENT DATE] (the "Agreement"), under which [AGREEMENT SUBJECT] in connection with our
[BRAND NAME] products. Section [BASIS SECTION] of the Agreement governs payment.

2. THE AMOUNT DEMANDED
[COMPANY NAME] demands payment of [AMOUNT DEMANDED].

That amount is calculated as follows: [AMOUNT CALCULATION].

3. WHAT IT RELATES TO
The amount relates to [INVOICE REFERENCES]. Copies, together with an account statement and the
supporting records, are enclosed.

Items we do not accept: [DISPUTED ITEMS]. If you rely on any deduction, chargeback or credit that
is not shown in Section 2, please provide the documentation supporting it, so the account can be
reconciled against records rather than recollection.

4. DEADLINE
Please ensure payment of [AMOUNT DEMANDED] is received by [PAYMENT DEADLINE].

5. HOW TO PAY
Payment should be made by [PAYMENT METHOD], to [REMITTANCE DETAILS].

6. IF YOU DISPUTE ANY PART OF THIS
If you dispute any invoice, amount or figure in this letter, please tell us in writing by
[DISPUTE RESPONSE DEADLINE], identifying the item, the amount and the reason, and enclosing the
documents you rely on. We would rather correct a genuine error now than argue about it later, and
we will review anything you send promptly. A dispute over part of the amount does not affect the
part that is not disputed, which remains due.

7. IF PAYMENT IS NOT RECEIVED
If payment is not received by the deadline in Section 4, and no agreed arrangement is in place, we
intend to [INTENDED NEXT STEP]. This letter states our position and what we intend to do. It is
not a statement about how any dispute would be decided.

8. SHIPMENTS
[SHIPMENT POSITION]. This applies regardless of any order already placed but not yet shipped,
except where the Agreement provides otherwise.

9. RESERVATION OF RIGHTS
Nothing in this letter waives, limits, modifies, releases or elects any right, remedy, claim or
defence available to [COMPANY NAME] under the Agreement or otherwise, all of which are expressly
reserved. Accepting a part payment, agreeing an arrangement, or extending any deadline does not
waive the balance or any right in respect of it. No delay in making this demand is a waiver. Any
waiver by us must be in writing and signed by an authorised officer of [COMPANY NAME].

10. HOW TO RESPOND
Please direct any response to [RESPONSE CONTACT NAME], [RESPONSE CONTACT DETAILS], and copy any
address the Agreement requires for notices to us. If it would be quicker to speak, we are happy to
take a call before the deadline.


Sincerely,



_______________________________________
[SIGNATORY NAME]
[SIGNATORY TITLE]
[COMPANY NAME]


Enclosures: [ENCLOSURES]
`,
  },
];

export default NOTICE_TEMPLATES;
